import { Link } from "react-router-dom";
import { Leaf, Check, ArrowUpRight, Heart, Sun, Sprout, FlaskConical } from "lucide-react";
import PageHero from "@/components/site/PageHero";

const approach = [
  { Icon: Sprout, title: "Ursachen verstehen", text: "Wir schauen ganzheitlich auf deine Situation, nicht nur auf einzelne Symptome, sondern auf das große Ganze." },
  { Icon: Leaf, title: "Gezielte Ernährung", text: "Ich empfehle passend zu deinem Beschwerdebild gezielt Lebensmittel und Nahrungsergänzungen, um deinen Körper bestmöglich zu unterstützen." },
  { Icon: FlaskConical, title: "Regelmäßige Beratungsgespräche", text: "Durch regelmäßigen Austausch begleite ich dich nah und persönlich - für mehr Sicherheit und Unterstützung in der Umsetzung." },
  { Icon: Sun, title: "Alltagstauglich", text: "Wir entwickeln Gewohnheiten, die wirklich in deinen Alltag passen - mit einfachen Tipps für eine entspannte Umsetzung." },
];

const topics = [
  "Chronische Müdigkeit & Erschöpfung",
  "Hautprobleme",
  "Allergien & Unverträglichkeiten",
  "Verdauungsbeschwerden",
  "Brainfog & Konzentrationsprobleme",
  "Heißhunger und schwieriges Essverhalten",
  "Stressregulation & Stimmungsschwankungen",
  "Infektanfälligkeit",
];

const packages = [
  {
    name: "Für Einsteiger",
    price: "25,00€",
    duration: "Dein E-Book",
    description: "Der perfekte Einstieg in den Ansatz von Anthony William. Du erhältst ein E-Book mit wichtigen Grundlagen, alltagstauglichen Tipps, hilfreichen Routinen und einfachen Impulsen für die Umsetzung im Alltag.",
    accent: "bg-sage-100 border-sage-200",
  },
  {
    name: "Basispaket",
    price: "690,00€",
    duration: "4 Wochen",
    description: "Individuelle Beratung mit persönlicher Begleitung über 3 Monate. In insgesamt 4 Sitzungen schauen wir gemeinsam auf deine Beschwerden, deine Ernährung und deinen Alltag, um passende und alltagstaugliche Veränderung für mehr Wohlbefinden zu entwickeln.",
    accent: "bg-honey-100 border-honey-200",
    highlight: true,
  },
  {
    name: "Langzeitbegleitung",
    price: "990,00€",
    duration: "6 Monate",
    description: "Über 6 Monate begleite ich dich in insgesamt 8 Sitzungen individuell und alltagsnah. Geeignet für komplexere Beschwerden, tiefere Veränderungen und eine intensivere Umsetzung im Alltag.",
    accent: "bg-clay-100 border-clay-200",
  },
];

export default function HealthPage() {
  return (
    <>
      <PageHero
        chapter="I"
        eyebrow="Gesundheit"
        title="Ernährungs- &"
        italic="Gesundheitscoaching."
        intro="Mit einer gezielten Auswahl an Lebensmitteln unterstütze ich dich dabei, deinen Körper bei Beschwerden alltagstauglich und ganzheitlich zu begleiten."
        tone="sage"
        image="https://static.prod-images.emergentagent.com/jobs/95c0696e-38c6-4b0c-80e9-86ee9a68795b/images/7e7a04fafc8a154c175a87e02095ee2aa86d6340079bffae9238cc4a1c91a88c.png"
      />

      {/* Approach */}
      <section className="relative py-24 md:py-32 bg-bone-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <div className="flex items-center gap-4">
              <span className="font-mono-fine text-[11px] tracking-[0.28em] uppercase text-ink-500">§ 01</span>
              <span className="h-[1px] w-10 bg-sage-400" />
              <span className="font-mono-fine text-[11px] tracking-[0.28em] uppercase text-sage-600">Mein Ansatz</span>
            </div>
            <h2 className="mt-6 font-display font-light text-4xl md:text-5xl lg:text-[3.75rem] leading-[0.98] tracking-[-0.035em] text-ink-900">
              Was dich <span className="italic font-normal text-sage-500">erwartet</span>.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {approach.map(({ Icon, title, text }) => (
              <div key={title} className="bg-sage-50 border border-sage-200 rounded-[1.5rem] p-7 hover:-translate-y-1 hover:shadow-lift transition-all duration-500">
                <div className="w-12 h-12 rounded-full bg-sage-300 text-sage-700 flex items-center justify-center">
                  <Icon strokeWidth={1.75} className="w-5 h-5" />
                </div>
                <h3 className="mt-5 font-display text-[1.4rem] text-ink-900">{title}</h3>
                <p className="mt-2 text-ink-700 text-[14.5px] leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Topics */}
      <section className="relative py-24 md:py-32 bg-sage-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4">
              <span className="font-mono-fine text-[11px] tracking-[0.28em] uppercase text-ink-500">§ 02</span>
              <span className="h-[1px] w-10 bg-sage-400" />
              <span className="font-mono-fine text-[11px] tracking-[0.28em] uppercase text-sage-600">Themen</span>
            </div>
            <h2 className="mt-6 font-display font-light text-4xl md:text-5xl lg:text-[3.75rem] leading-[0.98] tracking-[-0.035em] text-ink-900">
              Wobei ich dich <span className="italic font-normal">unterstützen</span> kann.
            </h2>
            <p className="mt-6 text-ink-700 leading-relaxed max-w-md">
              Hinter Beschwerden steckt oft mehr als ein einzelnes Symptom. Hier findest du einige der Themen, die Menschen zu mir führen.
            </p>
            <Link
              to="/kontakt"
              className="mt-8 inline-flex items-center gap-2 text-[14px] tracking-wide text-sage-700 link-underline"
            >
              Dein Thema ist nicht dabei? Lass uns sprechen
              <ArrowUpRight strokeWidth={1.5} className="w-4 h-4" />
            </Link>
          </div>

          <div className="lg:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-3">
              {topics.map((t) => (
                <li key={t} className="flex items-center gap-3 p-5 bg-bone-50 rounded-2xl border border-sage-200">
                  <span className="w-7 h-7 rounded-full bg-sage-300 text-sage-700 flex items-center justify-center shrink-0">
                    <Check strokeWidth={2} className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-ink-900 text-[15px]">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="relative py-24 md:py-32 bg-bone-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <div className="flex items-center gap-4">
              <span className="font-mono-fine text-[11px] tracking-[0.28em] uppercase text-ink-500">§ 03</span>
              <span className="h-[1px] w-10 bg-honey-400" />
              <span className="font-mono-fine text-[11px] tracking-[0.28em] uppercase text-honey-600">Begleitung</span>
            </div>
            <h2 className="mt-6 font-display font-light text-4xl md:text-5xl lg:text-[3.75rem] leading-[0.98] tracking-[-0.035em] text-ink-900">
              So können wir <span className="italic font-normal">zusammen­arbeiten</span>.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {packages.map((p) => (
              <div
                key={p.name}
                className={`relative rounded-[1.75rem] p-8 border ${p.accent} ${p.highlight ? "ring-2 ring-honey-300 scale-[1.02]" : ""} hover:-translate-y-1 hover:shadow-lift transition-all duration-500`}
              >
                {p.highlight && (
                  <span className="absolute -top-3 left-8 px-3 h-7 inline-flex items-center rounded-full bg-honey-500 text-ink-900 font-mono-fine text-[10px] tracking-[0.22em] uppercase">
                    Beliebt
                  </span>
                )}
                <div className="font-mono-fine text-[11px] tracking-[0.22em] uppercase text-ink-500">
                  {p.duration}
                </div>
                <h3 className="mt-2 font-display font-light text-[2rem] text-ink-900 leading-tight">
                  {p.name}
                </h3>
                <div className="mt-4 font-display text-3xl text-ink-900">{p.price}</div>
                <p className="mt-4 text-ink-700 leading-relaxed">{p.description}</p>
                <Link
                  to="/kontakt"
                  className="mt-6 inline-flex items-center gap-2 text-[13px] tracking-wide text-ink-900 link-underline"
                >
                  Anfragen <ArrowUpRight strokeWidth={1.5} className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-24 bg-sage-100">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <Heart strokeWidth={1.5} className="w-8 h-8 text-sage-600 mx-auto" />
          <h2 className="mt-6 font-display font-light text-4xl md:text-5xl text-ink-900 leading-[1] tracking-[-0.03em]">
            Bereit für den <span className="italic">ersten Schritt</span>?
          </h2>
          <p className="mt-5 text-ink-700 max-w-xl mx-auto">
            Das Erstgespräch ist kostenfrei und unverbindlich. Wir schauen gemeinsam, ob und wie wir zueinander passen.
          </p>
          <Link
            to="/kontakt"
            className="mt-8 group inline-flex items-center gap-3 pl-6 pr-2 h-14 rounded-full bg-ink-900 hover:bg-sage-600 text-bone-50 text-[14px] tracking-wide transition-all"
          >
            Gespräch vereinbaren
            <span className="w-11 h-11 rounded-full bg-honey-400 text-ink-900 flex items-center justify-center transition-transform group-hover:rotate-45">
              <ArrowUpRight strokeWidth={1.75} className="w-4 h-4" />
            </span>
          </Link>
        </div>
      </section>
    </>
  );
}
